import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, CircularProgress, Grid, styled, Typography } from "@mui/material";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { fetchDispatchDesks } from "src/redux/reducers/DispatchDeskReducer";
import { DispatchDeskCard } from "./DispatchDeskCard";
import { UnassignedFlightsPanel } from "./UnassignedFlightsPanel";

// ---------------------------------------------------------------------------
// Styled root container
// ---------------------------------------------------------------------------

const StyledRoot = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "100%",
  overflow: "auto",
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.default,
}));

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface IDispatchDeskWindowProps {
  id?: string;
  windowProps?: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DispatchDeskWindow(props: IDispatchDeskWindowProps): JSX.Element {
  const dispatch = useDispatch();
  const { desks, loading } = useSelector((state: IRootState) => state.dispatchDesks);

  // Fetch desks on mount
  useEffect(() => {
    dispatch(fetchDispatchDesks());
  }, [dispatch]);

  if (loading && desks.length === 0) {
    return (
      <StyledRoot>
        <Grid container justifyContent="center" alignItems="center" sx={{ height: "100%" }}>
          <CircularProgress />
        </Grid>
      </StyledRoot>
    );
  }

  if (!loading && desks.length === 0) {
    return (
      <StyledRoot>
        <Box sx={{ display: "flex", height: "100%", width: "100%" }}>
          <Grid container justifyContent="center" alignItems="center" sx={{ flex: 1 }}>
            <Typography variant="h6" color="textSecondary">
              No dispatch desks configured.
            </Typography>
          </Grid>
          <UnassignedFlightsPanel />
        </Box>
      </StyledRoot>
    );
  }

  return (
    <StyledRoot>
      <Box sx={{ display: "flex", height: "100%", width: "100%" }}>
        {/* Desk cards – scrollable, fills remaining space */}
        <Box sx={{ flex: 1, overflow: "auto", padding: (theme) => theme.spacing(0) }}>
          <Grid container spacing={1} sx={{ height: "100%" }}>
            {desks.map((desk) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={desk._id}>
                <DispatchDeskCard desk={desk} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Unassigned flights panel – collapsible right sidebar */}
        <UnassignedFlightsPanel />
      </Box>
    </StyledRoot>
  );
}
